import Cookies from 'js-cookie';
import storage from 'proxy-storage';
import urlParameter from '../utils/url/urlParameter';

const COOKIE_NAME = 'shareLocalStorageWithHealthcare';

/**
 * @public
 *
 * Save localStorage on a cookie to be read on the following redirect
 */
export function shareLocalStorageWithHealthcare() {
  const hcStorage = storage.getItem('hc');
  const subsetOfHcStorage = getSubsetOfHcStorage(hcStorage);
  const cookieContent = JSON.stringify(subsetOfHcStorage);

  saveCookie(COOKIE_NAME, cookieContent);

  validateSavedCookie({
    cookieName: COOKIE_NAME,
    cookieContentLength: cookieContent.length,
    hostnameAndPath: window.location.hostname + window.location.pathname,
    sessionId: hcStorage.healthcareSession,
    source: hcStorage.source,
  });
}

/**
 * @public
 *
 * Save localStorage on a cookie to be read on the following redirect
 */
export function updateLocalStorage() {
  const value = readCookie(COOKIE_NAME) || '';
  const cookieSession = readCookie('healthcareSession');

  const isSurvey = window.location.pathname.includes('/survey');

  const isFromUmbral = document !== undefined && !document.referrer.includes('/survey') && (document.referrer.includes('medicare.') || document.referrer.includes('quotes.'));

  const isSRCParamPresent = urlParameter('src') !== '';

  const isLocalStorageCookieEmpty = value.length === 0;

  logCookieSessionWarn(isFromUmbral && isLocalStorageCookieEmpty && isSurvey && isSRCParamPresent, cookieSession);

  if (value.length > 0) {
    const hcFromCookie = JSON.parse(value);
    if (cookieSession === hcFromCookie.healthcareSession) {
      storage.setItem('hc', hcFromCookie);
      if (isSurvey) {
        document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=healthcare.com; path=/;`;
      }
    }
  }
}

/**
 * Returns a subset of the properties from hcStorage.
 * It does not alter the hcStorage object.
 *
 * @param {Object} hcStorage HC object from local storage.
 * @returns {Object} Subset of HC object.
 */
function getSubsetOfHcStorage(hcStorage) {
  const {
    answers,
    answersByStep,
    cache,
    fastTrack,
    flowCatch,
    // eslint-disable-next-line camelcase
    invoca_phone,
    trafficContext,
    ...subsetOfHcStorage
  } = hcStorage;

  if (answers) {
    const { disclaimer, ...otherAnswers } = answers;

    return {
      ...subsetOfHcStorage,
      answers: otherAnswers,
    };
  }

  return subsetOfHcStorage;
}

/**
 * @public
 *
 * Log to Raygun when shareLocalStorageWithHealthcare is empty and referrer site is El Umbral
 * @param {Boolean} sendLog send or not notification to Raygun
 * @param {String} cookieSession healthcareSessionId to attach to notification
 */
function logCookieSessionWarn(sendLog, cookieSession) {
  if (sendLog === false) {
    return;
  }

  if (window !== undefined) {
    window.rg4js('send', {
      error: new Error('shareLocalStorageWithHealthcare from El Umbral is undefined'),
      customData: [{healthcareSessionId: cookieSession}],
    });
  }
}

/**
 * Validates that a cookie was saved.
 * @param {Object} properties Cookie to validate.
 */
function validateSavedCookie(properties) {
  const { cookieName, cookieContentLength, hostnameAndPath, sessionId, source } = properties;
  const value = readCookie(cookieName);
  if (!value) {
    window.rg4js('send', {
      error: new Error('Could not save cookie'),
      customData: [{
        cookieName,
        cookieContentLength,
        hostnameAndPath,
        sessionId,
        source,
      }],
    });
  }
}

/**
 * Saves a cookie.
 * @param {String} name Name of the cookie.
 * @param {String} value Value of the cookie.
 */
function saveCookie(name, value) {
  const cookies = buildNonEncodingCookies();
  const options = buildCookieOptions();
  cookies.set(name, value, options);
}

/**
 * Retrieves a value from the document cookies.
 * @param {String} name Name of the cookie.
 * @returns {String} Value of the cookie.
 */
function readCookie(name) {
  const cookies = buildNonEncodingCookies();
  return cookies.get(name);
}

/**
 * Returns a Cookie manager that does not encodes the cookie value.
 * @returns {Cookies} Cookie manager that does not encodes the cookie value.
 */
function buildNonEncodingCookies() {
  const returnWithoutEncoding = value => value;
  const withoutEncodingConverter = {
    write: returnWithoutEncoding,
    read: returnWithoutEncoding,
  };
  return Cookies.withConverter(withoutEncodingConverter);
}

/**
 * Returns options object including expiring in one hour.
 * @returns {Object} Options object.
 */
function buildCookieOptions() {
  const expireDate = new Date();
  expireDate.setHours(expireDate.getHours() + 1);

  return {
    domain: '.healthcare.com',
    expires: expireDate,
  };
}
